import { Box, Flex, Skeleton, Stack, Title, useMantineColorScheme } from "@mantine/core";
import { useDisclosure, useLocalStorage } from "@mantine/hooks";
import { useTranslation } from "react-i18next";

import { ContentLocker } from "../ContentLocker";
import { formatNestedValue, getMarketHeadlinesData } from "./functions";

import { DataGrid } from "@/components/elements";
import { defaultCurrency } from "@/lib/currency";
import { useMarketCar } from "@/services/market/get-market-car";
import { usePlansUser } from "@/services/plans/get-user-plan";
import { opacityForAccess } from "@/utils/contetLocker";

import type { Currency } from "@/types/currency";
import type { MantineStyleProps } from "@mantine/core";
import type { FC } from "react";

export type CarMarketHeadlinesProps = {
  carId: string;
} & MantineStyleProps;

export const CarMarketHeadlines: FC<CarMarketHeadlinesProps> = ({ carId, ...props }) => {
  const { t } = useTranslation();
  const { data, isLoading } = useMarketCar({ carId });
  const [showPlanModal, { open: openPlanModal, close: closePlanModal }] = useDisclosure();

  const { data: plans } = usePlansUser();

  /* Theme */
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === "dark";

  const [currency] = useLocalStorage<Currency>({
    key: "currency",
    defaultValue: defaultCurrency,
  });

  /* handlers */
  const fv = formatNestedValue(data, currency);
  const items = getMarketHeadlinesData(t, fv, data, currency);

  const canAccess = plans?.config.ad_listing_analytics ?? false;

  return (
    <Stack {...props} style={{ position: "relative" }}>
      <Title order={3}>{t("car.market_headlines")}</Title>
      <Box pos="relative" h={canAccess ? "fit-content" : 300}>
        <Skeleton visible={isLoading}>
          <Box style={{ opacity: opacityForAccess(canAccess, isDark) }}>
            <DataGrid itemSets={items} />
          </Box>
        </Skeleton>
        {!canAccess && !isLoading && (
          <Flex
            align="center"
            justify="center"
            pos="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            style={{ zIndex: 10 }}
          >
            <Box
              pos="absolute"
              top={0}
              left={0}
              right={0}
              bottom={0}
              style={() => ({
                pointerEvents: "none",
                zIndex: 9,
                background: isDark
                  ? "linear-gradient(to bottom, rgba(27, 27, 27, 0.1), rgba(25,25,25,0.8))"
                  : "linear-gradient(to bottom, rgba(255,255,255,0.1), rgba(255,255,255,0.9))",
              })}
            />
            <Box pos="relative" style={{ zIndex: 10 }}>
              <ContentLocker
                openPlanModal={openPlanModal}
                closePlanModal={closePlanModal}
                showPlanModal={showPlanModal}
              />
            </Box>
          </Flex>
        )}
      </Box>
    </Stack>
  );
};
